import React, { useMemo } from 'react';
import { View } from 'react-native';
import { toQR } from 'toqr';
import { radius } from '@/theme';
import type { Appointment, BookingStatus } from '@/data/types';
import { ScaledSheet } from '@/theme/responsive';

/** Light modules around the code, as the QR standard asks, so phone scanners lock on reliably. */
const QUIET_ZONE = 4;

/** The reference printed under every pass: the booking id's first 8 characters. */
export const bookingReference = (bookingId: string) => bookingId.slice(0, 8).toUpperCase();

const STATUS_TEXT: Record<BookingStatus, string> = {
  PENDING: 'Pending',
  CONFIRMED: 'Confirmed',
  IN_PROGRESS: 'In progress',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
  NO_SHOW: 'No-show',
};

/**
 * What the booking pass's QR carries: the booking in plain words, so any phone's scanner shows the
 * shop, stylist, service, time and total without needing this app. Built from the booking as it is
 * right now, so the code (and what a scan shows) changes when the booking is confirmed, moved or
 * cancelled. Kept short and plain English: a longer code means smaller squares that are harder to scan.
 */
export function bookingQrPayload(apt: Pick<Appointment, 'shopId' | 'bookingCode' | 'status' | 'shopName' | 'stylistName' | 'services' | 'date' | 'time' | 'durationMin' | 'total'>): string {
  const services = apt.services.map((s) => s.name).join(', ');
  const lines = [
    `SalunBD booking ${apt.bookingCode}`,
    `Status: ${STATUS_TEXT[apt.status] ?? apt.status}`,
    apt.shopName && apt.shopName !== apt.stylistName ? `Shop: ${apt.shopName}` : '',
    apt.stylistName ? `Stylist: ${apt.stylistName}` : '',
    services ? `Service: ${services.length > 60 ? `${services.slice(0, 57)}...` : services}` : '',
    `When: ${apt.date}, ${apt.time} (${apt.durationMin} min)`,
    `Total: Tk ${apt.total} (cash at the shop)`,
  ];
  return lines.filter(Boolean).join('\n');
}

/**
 * A real, scannable QR code. With `value` it draws that text (the booking pass passes the booking's
 * details, see bookingQrPayload); with only `seed` it draws the short booking reference. Staff can
 * scan the pass, or read the code aloud, and get the same booking. It used to be a decorative
 * pattern seeded from the id that no scanner could read.
 *
 * Always dark on white (scanners need that contrast, so it ignores dark mode). Each row's runs of
 * dark modules are drawn as single blocks, which keeps a 21x21 code to a few dozen views.
 */
export function QrCode({ seed, value, size = 160 }: { seed: string; value?: string; size?: number }) {
  const { side, runs } = useMemo(() => {
    const modules = toQR(value ?? bookingReference(seed));
    const n = Math.round(Math.sqrt(modules.length));
    const rows: { start: number; length: number }[][] = [];
    for (let r = 0; r < n; r++) {
      const row: { start: number; length: number }[] = [];
      let c = 0;
      while (c < n) {
        if (!modules[r * n + c]) {
          c++;
          continue;
        }
        const start = c;
        while (c < n && modules[r * n + c]) c++;
        row.push({ start, length: c - start });
      }
      rows.push(row);
    }
    return { side: n + QUIET_ZONE * 2, runs: rows };
  }, [seed, value]);

  const cell = size / side;

  return (
    <View
      style={[styles.container, { width: size, height: size }]}
      accessible
      accessibilityRole="image"
      accessibilityLabel={`QR code for booking ${bookingReference(seed)}`}>
      {runs.map((row, r) =>
        row.map((run) => (
          <View
            key={`${r}-${run.start}`}
            style={[
              styles.module,
              { top: (r + QUIET_ZONE) * cell, left: (run.start + QUIET_ZONE) * cell, width: run.length * cell + 0.5, height: cell + 0.5 },
            ]}
          />
        )),
      )}
    </View>
  );
}

const styles = ScaledSheet.create({
  container: { borderRadius: radius.sm, overflow: 'hidden', backgroundColor: '#FFFFFF' },
  module: { position: 'absolute', backgroundColor: '#000000' },
});
